import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaPlus } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
import { formatDateDDMMYYEmpty } from '../../../components/common/dateUtils';

const ROWS_PER_PAGE = 20;

const WarehouseEntryList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch paginated data from server
  const fetchData = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/warehouse/warehouse-entry-get?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  // Pagination
  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  // Using the common formatDateDDMMYYEmpty function from dateUtils

  const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '';
    return parseFloat(num).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-lg font-medium text-gray-800">Vessel to Storage Movement</h2>
        <div className="flex gap-1 w-full sm:w-auto justify-end">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className="flex items-center gap-1 bg-[#10a5e9] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
            onClick={() => navigate('/warehouse-entry/new')}
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : data.length === 0 ? (
        <NoRecordFound message="No warehouse entries found." />
      ) : (
        <>
          <div className="overflow-x-auto rounded border border-gray-50">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Purchase Contract</th>
                  <th className="px-3 py-2 text-left font-medium">Vessel Name</th>
                  <th className="px-3 py-2 text-left font-medium">Commodity</th>
                  <th className="px-3 py-2 text-left font-medium">CMA</th>
                  <th className="px-3 py-2 text-left font-medium">Warehouse</th>
                  <th className="px-3 py-2 text-left font-medium">Received Date</th>
                  <th className="px-3 py-2 text-left font-medium">Quantity (MT)</th>
                  <th className="px-3 py-2 text-left font-medium">QC Status</th>
                  <th className="px-3 py-2 text-left font-medium">Grade</th>
                  <th className="px-3 py-2 text-left font-medium">Dockage %</th>
                  <th className="px-3 py-2 text-left font-medium">PSB %</th>
                  <th className="px-3 py-2 text-left font-medium">FM %</th>
                  <th className="px-3 py-2 text-left font-medium">Unit Cost (INR)</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={row.warehouse_entry_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-1">{row.purchase_contract_no || row.order_id}</td>
                    <td className="px-3 py-1">{row.vessel_name || 'N/A'}</td>
                    <td className="px-3 py-1">
                      {row.commodity ? (
                        <span title={row.commodity_origin ? `${row.commodity} (${row.commodity_origin})` : row.commodity}>
                          {row.commodity}
                          {row.commodity_origin && <span className="text-gray-500 text-xs ml-1">({row.commodity_origin})</span>}
                        </span>
                      ) : 'N/A'}
                    </td>
                    <td className="px-3 py-1">{row.cma_name}</td>
                    <td className="px-3 py-1">{row.warehouse_name}</td>
                    <td className="px-3 py-1">{formatDateDDMMYYEmpty(row.received_date)}</td>
                    <td className="px-3 py-1">{formatNumber(row.received_qty_mt)}</td>
                    <td className="px-3 py-1">
                      <span className={`px-2 py-1 text-xs rounded ${
                        row.qc_status === 'PASSED' ? 'bg-green-100 text-green-800' :
                        row.qc_status === 'HOLD' ? 'bg-yellow-100 text-yellow-800' :
                        row.qc_status === 'REJECTED' ? 'bg-red-100 text-red-800' : ''
                      }`}>
                        {row.qc_status}
                      </span>
                    </td>
                    <td className="px-3 py-1">{row.grade}</td>
                    <td className="px-3 py-1">{formatNumber(row.dockage_pct)}</td>
                    <td className="px-3 py-1">{formatNumber(row.psb_pct)}</td>
                    <td className="px-3 py-1">{formatNumber(row.fm_pct)}</td>
                    <td className="px-3 py-1">{formatNumber(row.unit_cost_inr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 text-sm">
              <div className="text-gray-600">
                Showing {((page - 1) * ROWS_PER_PAGE) + 1} to {Math.min(page * ROWS_PER_PAGE, total)} of {total} entries
              </div>
              <div className="flex gap-1">
                <button
                  onClick={handlePrevPage}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1 border border-gray-300 rounded bg-gray-50">
                  {page} of {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={page === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WarehouseEntryList;
